import React from 'react';
import { Zap, TrendingUp, Shield, Heart, Clock, Award } from 'lucide-react';

const BenefitsSectionPremium = () => {
  const benefits = [
    {
      icon: Zap,
      title: 'Enhanced Therapeutic Power',
      description: 'Amplify growth factor release and cellular activation for superior regenerative outcomes',
      stat: '+150%',
      label: 'Efficacy Increase'
    },
    {
      icon: TrendingUp,
      title: 'Maximized Exosome Production',
      description: 'Naturally stimulate the production of 299.3 billion exosomes per ml for the best therapeutic treatment',
      stat: '5X',
      label: 'More Exosomes'
    },
    {
      icon: Shield,
      title: 'Pharmaceutical-Grade Safety',
      description: 'Proven safety profile and zero adverse reactions',
      stat: '100%',
      label: 'Safety Record'
    },
    {
      icon: Heart,
      title: 'Superior Patient Experience',
      description: 'Reduced injection discomfort and faster recovery times drive higher satisfaction',
      stat: '95%',
      label: 'Patient Satisfaction'
    },
    {
      icon: Clock,
      title: 'Optimized Treatment Efficiency',
      description: 'Reduce session frequency by 40% while achieving better outcomes',
      stat: '-40%',
      label: 'Fewer Sessions'
    },
    {
      icon: Award,
      title: 'Proven Clinical Results',
      description: 'Backed by rigorous clinical studies and trusted by leading practitioners worldwide',
      stat: '500+',
      label: 'Partner Clinics'
    }
  ];

  return (
    <section className="py-32 px-6 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Premium background accents */}
      <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-r from-teal-50/40 to-transparent"></div>
      <div className="absolute bottom-0 right-0 w-1/2 h-full bg-gradient-to-l from-teal-50/30 to-transparent"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section header with premium styling */}
        <div className="text-center mb-20 animate-fade-in">
          <span className="inline-block text-teal-500 font-medium text-sm tracking-wider uppercase mb-4">
            Clinical Advantages
          </span>
          <h2 className="text-5xl md:text-6xl font-light text-gray-900 mb-6 leading-tight" style={{ fontFamily: 'Raleway, sans-serif' }}>
            Transform Patient Outcomes
            <br />
            <span className="font-normal text-teal-500">Elevate Your Practice</span>
          </h2>
          <p className="text-xl text-gray-600 font-light max-w-3xl mx-auto leading-relaxed">
            Experience the competitive edge of next-generation biomodulation technology
          </p>
        </div>

        {/* Premium benefits grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <div
                key={index}
                className="group relative bg-white rounded-3xl p-8 shadow-soft hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-gray-100 hover:border-teal-200"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Premium icon container */}
                <div className="mb-6 relative">
                  <div className="w-16 h-16 bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-soft">
                    <Icon className="w-8 h-8 text-teal-500" strokeWidth={1.5} />
                  </div>
                  {/* Floating badge */}
                  <div className="absolute -top-2 -right-2 bg-teal-500 text-white text-xs font-medium px-3 py-1 rounded-full shadow-lg">
                    {benefit.stat}
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-2xl font-light mb-3 text-gray-900 group-hover:text-teal-600 transition-colors duration-300">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 font-light leading-relaxed mb-4">
                  {benefit.description}
                </p>
                
                {/* Stat label */}
                <div className="text-sm text-teal-500 font-medium">
                  {benefit.label}
                </div>

                {/* Hover accent */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-400 to-teal-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 rounded-b-3xl"></div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default BenefitsSectionPremium;
